import { useEffect, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFinanceDialogBounds } from "@/components/finance/financeDialogViewport";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { canAccessFinanceRoute } from "@/finance/financeNav";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  FinancePageHeader,
  FinanceMetricCard,
  FinanceMetricGrid,
  FinanceSection,
  FinanceLoadingState,
  FinanceErrorState,
  FinanceAccessDeniedState,
  FinanceResponsiveTable,
  FinanceStatusBadge,
} from "@/components/finance/ui";
import {
  AlertCircle,
  AlertTriangle,
  Boxes,
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  Eye,
  FileText,
  FileClock,
  GitCompareArrows,
  HelpCircle,
  History,
  Loader2,
  Lock,
  PieChart,
  Plus,
  RefreshCw,
  Warehouse,
  Search,
  Save,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
} from "lucide-react";
import {
  sortProjectMarginRows,
  toggleProjectMarginSort,
} from "@/finance/projectsMarginSorting";
import type { ProjectMarginSort, ProjectMarginSortKey } from "@/finance/projectsMarginSorting";
import { PROJECT_MARGIN_CATEGORIES } from "@shared/finance/projectMarginCategories";

const projectMetrics = [
  "Contrato original",
  "Contrato vigente",
  "Ingreso planificado",
  "Ingreso facturado",
  "Ingreso cobrado",
  "Presupuesto de coste aprobado",
  "Coste comprometido",
  "Coste real",
  "Margen previsto",
  "Margen real",
  "Desviacion",
  "Costes sin clasificar",
];

const futureCostChain = [
  "Presupuesto aprobado",
  "Pedido de compra",
  "Recepcion / albaran",
  "Factura proveedor",
  "Coste real",
];

const budgetPrinciples = [
  "El presupuesto aprobado queda versionado.",
  "Una desviacion no modifica silenciosamente el presupuesto historico.",
  "El coste real alimenta senales para revisar Product Catalog.",
  "Las comparativas futuras se hacen contra pedidos, recepciones y facturas proveedor.",
];

const euro = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

function formatCents(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return "-";
  return euro.format(cents / 100);
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined) return "-";
  return `${value.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("es-ES");
}

function formatCentsForInput(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return "";
  return (cents / 100).toFixed(2);
}

function parseEuroDraftToCents(value: string) {
  const normalized = value.trim().replace(",", ".");
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(normalized);
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
}

function SortableProjectMarginHead({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: ProjectMarginSortKey;
  sort: ProjectMarginSort | null;
  onSort: (key: ProjectMarginSortKey) => void;
  className?: string;
}) {
  const active = sort?.key === sortKey;
  const direction = active ? sort.direction : null;
  const directionLabel = direction === "desc" ? "de mayor a menor" : direction === "asc" ? "de menor a mayor" : "sin ordenar";
  const alignmentClass = className?.includes("text-right")
    ? "justify-end"
    : className?.includes("text-center")
      ? "justify-center"
      : "justify-start";

  return (
    <TableHead className={`${className ?? ""} p-0`} scope="col" aria-sort={direction === "desc" ? "descending" : direction === "asc" ? "ascending" : "none"}>
      <button
        type="button"
        className={`flex w-full cursor-pointer items-center h-10 gap-1.5 rounded-sm px-2 outline-none transition-colors hover:text-ods-navigation focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${alignmentClass}`}
        onClick={() => onSort(sortKey)}
        title={`Ordenar ${label} ${directionLabel}`}
        aria-label={`Ordenar por ${label}. Estado actual: ${directionLabel}`}
      >
        <span>{label}</span>
        {direction === "desc" ? <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" /> : direction === "asc" ? <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
      </button>
    </TableHead>
  );
}

function AccessDenied({ title }: { title: string }) {
  return (
    <DashboardLayout>
      <div className="flex min-h-[55vh] items-center justify-center">
        <Card className="max-w-lg border-dashed">
          <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <Lock className="h-8 w-8 text-muted-foreground" />
            <div>
              <h1 className="text-xl font-semibold">Acceso no disponible</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {title} todavia no esta disponible para tu perfil.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export function FinanceProjectsMarginView() {
  const { user } = useAuth();
  const allowed = user ? canAccessFinanceRoute("/accounting/projects-margin", user.role) : false;
  const query = trpc.finance.projectsMargin.list.useQuery(undefined, { enabled: allowed });
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<ProjectMarginSort | null>(null);
  const [detailBudgetId, setDetailBudgetId] = useState<number | null>(null);
  const detailBudgetBounds = useFinanceDialogBounds(detailBudgetId !== null, 1600);
  const [adjustmentDrafts, setAdjustmentDrafts] = useState<Record<string, { amount: string; note: string }>>({});
  const detail = trpc.finance.budgetDocuments.getById.useQuery(
    { id: detailBudgetId ?? 1 },
    { enabled: allowed && detailBudgetId !== null },
  );
  const saveMarginAdjustment = trpc.finance.budgetDocuments.saveMarginAdjustment.useMutation({
    onSuccess: async () => {
      toast.success("Ajuste manual guardado");
      await Promise.all([
        utils.finance.projectsMargin.list.invalidate(),
        detailBudgetId ? utils.finance.budgetDocuments.getById.invalidate({ id: detailBudgetId }) : Promise.resolve(),
      ]);
    },
    onError: (error) => toast.error(`No se pudo guardar el ajuste: ${error.message}`),
  });

  useEffect(() => {
    const summary = detail.data?.comparison?.categorySummary;
    if (!summary) return;
    setAdjustmentDrafts(Object.fromEntries(summary.map((category: any) => [category.code, {
      amount: (category.manualAdjustmentCents / 100).toFixed(2),
      note: category.manualAdjustmentNote ?? "",
    }])));
  }, [detail.data?.id, detail.data?.comparison?.categorySummary]);

  if (!allowed) {
    return (
      <FinanceAccessDeniedState
        title="Proyectos y margen"
        message="Proyectos y margen todavía no está disponible para tu perfil de usuario."
      />
    );
  }

  const rows = query.data?.rows ?? [];
  const totals = query.data?.totals;
  const normalizedSearch = search.trim().toLocaleLowerCase("es");
  const filteredRows = normalizedSearch
    ? rows.filter((row) => [row.expedienteCode, row.clientName, row.workDescription]
      .some((value) => String(value ?? "").toLocaleLowerCase("es").includes(normalizedSearch)))
    : rows;
  const sortedRows = sort ? sortProjectMarginRows(filteredRows, sort) : filteredRows;
  const handleSort = (key: ProjectMarginSortKey) => {
    setSort((current) => toggleProjectMarginSort(current, key));
  };
  const marginCategories = (detail.data as any)?.comparison?.categorySummary ?? [];
  const budgetLineCategories = (detail.data as any)?.comparison?.budgetLineCategories ?? [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          eyebrowIcon={PieChart}
          title="Proyectos y margen"
          description="Margen real por instalación a partir de ingresos facturados y costes imputados, sin duplicar facturas, dietas ni importaciones."
          updatedAt={query.data?.updatedAt}
          note={{
            title: "Entidad canonica: installations",
            message: "Esta fase no crea tabla Works, no duplica proyectos y no escribe costes históricos. Las importaciones ya cuentan a través de facturas proveedor o gastos manuales.",
            type: "amber",
          }}
        />

        {query.isLoading ? (
          <FinanceLoadingState cardsCount={4} />
        ) : query.isError ? (
          <FinanceErrorState
            title="Error al cargar margen por instalación"
            message={query.error.message}
            onRetry={() => query.refetch()}
          />
        ) : (
          <>
            {/* Max 4 primary KPI cards in first row desktop */}
            <FinanceMetricGrid maxColumns={4}>
              <FinanceMetricCard
                title="Ingreso facturado"
                amountCents={totals?.invoicedRevenueCents}
                subtitle="Facturación emitida acumulada"
              />
              <FinanceMetricCard
                title="Coste real"
                amountCents={totals?.actualCostCents}
                variant="rose"
                subtitle="Costes imputados acumulados"
              />
              <FinanceMetricCard
                title="Margen real"
                amountCents={totals?.actualMarginCents}
                variant={totals?.actualMarginCents != null && totals.actualMarginCents >= 0 ? "emerald" : "rose"}
                subtitle="Ingresos menos costes reales"
              />
              <FinanceMetricCard
                title="Margen %"
                amountFormatted={formatPercent(totals?.actualMarginPct)}
                variant={totals?.actualMarginPct != null && totals.actualMarginPct >= 0 ? "emerald" : "rose"}
                subtitle="Porcentaje sobre facturado"
              />
            </FinanceMetricGrid>

            <FinanceSection
              title="Desglose de expedientes"
              description="Presupuesto de venta, beneficio real y vínculos financieros por obra."
              icon={Warehouse}
            >
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative w-full sm:max-w-md">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Buscar por obra, expediente o cliente..."
                      aria-label="Buscar por obra, expediente o cliente"
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{filteredRows.length} de {rows.length} expedientes</p>
                </div>

                <FinanceResponsiveTable>
                  <Table className="min-w-[1180px]">
                    <TableHeader>
                      <TableRow>
                        <SortableProjectMarginHead label="Obra" sortKey="work" sort={sort} onSort={handleSort} className="w-[170px]" />
                        <SortableProjectMarginHead label="Cliente" sortKey="client" sort={sort} onSort={handleSort} className="min-w-[210px]" />
                        <SortableProjectMarginHead label="Estado" sortKey="status" sort={sort} onSort={handleSort} className="w-[130px]" />
                        <SortableProjectMarginHead label="Presupuesto de venta" sortKey="salesBudget" sort={sort} onSort={handleSort} className="w-[170px] text-right" />
                        <SortableProjectMarginHead label="Beneficio real" sortKey="realProfit" sort={sort} onSort={handleSort} className="w-[150px] text-right" />
                        <SortableProjectMarginHead label="Margen de beneficio" sortKey="profitMargin" sort={sort} onSort={handleSort} className="w-[160px] text-center" />
                        <SortableProjectMarginHead label="Vínculos" sortKey="links" sort={sort} onSort={handleSort} className="w-[170px]" />
                        <SortableProjectMarginHead label="Acciones" sortKey="actions" sort={sort} onSort={handleSort} className="w-[160px] text-right" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="h-28 text-center text-sm text-muted-foreground">
                            {rows.length === 0 ? "No hay expedientes disponibles." : "No hay expedientes que coincidan con la búsqueda."}
                          </TableCell>
                        </TableRow>
                      ) : sortedRows.map((row) => {
                        const positiveProfit = row.realProfitCents !== null && row.realProfitCents >= 0;
                        const marginTone = !row.hasBudget
                          ? "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-900"
                          : row.profitMarginPct !== null && row.profitMarginPct >= 0
                            ? "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:ring-emerald-900"
                            : row.profitMarginPct === null
                              ? "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-900"
                              : "bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:ring-rose-900";
                        const statusLabel = row.status === "in_progress" ? "Iniciada"
                          : row.status === "completed" ? "Completada"
                            : row.status === "cancelled" ? "Cancelada" : "Pendiente";

                        return (
                          <TableRow
                            key={row.installationId}
                            className={row.budgetId ? "cursor-pointer hover:bg-muted/60" : undefined}
                            tabIndex={row.budgetId ? 0 : undefined}
                            title={row.budgetId ? "Abrir presupuesto, gastos y margen por categoría" : undefined}
                            onClick={() => row.budgetId && setDetailBudgetId(row.budgetId)}
                            onKeyDown={(event) => {
                              if (row.budgetId && (event.key === "Enter" || event.key === " ")) {
                                event.preventDefault();
                                setDetailBudgetId(row.budgetId);
                              }
                            }}
                          >
                            <TableCell>
                              <div className="space-y-1.5">
                                <Badge className="bg-slate-800 font-mono text-xs text-white hover:bg-slate-800">
                                  {row.expedienteCode || row.installationId}
                                </Badge>
                                <p className="max-w-[220px] truncate text-[11px] text-muted-foreground" title={row.workDescription || ""}>
                                  {row.workDescription || "Sin descripción de obra"}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{row.clientName || "Cliente sin nombre"}</TableCell>
                            <TableCell><FinanceStatusBadge status={row.status || "pending"} labelOverride={statusLabel} /></TableCell>
                            <TableCell className="text-right font-semibold tabular-nums">{formatCents(row.salesBudgetCents)}</TableCell>
                            <TableCell className={`text-right font-semibold tabular-nums ${positiveProfit ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}>
                              {formatCents(row.realProfitCents)}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${marginTone}`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                {formatPercent(row.profitMarginPct)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1 text-[11px] font-medium">
                                <div className={`flex items-center gap-1.5 ${row.hasBudget ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
                                  {row.hasBudget ? <FileText className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                                  <span>{row.hasBudget ? "Presupuesto" : "Falta presupuesto"}</span>
                                </div>
                                <div className={`flex items-center gap-1.5 ${row.comparisonReady ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
                                  <GitCompareArrows className="h-3.5 w-3.5" />
                                  <span>{row.comparisonReady ? "Comparativa" : !row.actualMarginBasisVerified ? "Base fiscal pendiente" : "Falta comparativa"}</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5 whitespace-nowrap text-xs font-semibold"
                                onClick={(event) => { event.stopPropagation(); window.location.href = `/installations/${row.installationId}`; }}
                              >
                                Abrir instalación
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </FinanceResponsiveTable>
              </div>
            </FinanceSection>
          </>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
          <FinanceSection
            title="Campos de margen"
            description="Estructura de métricas financieras del proyecto"
            icon={Warehouse}
          >
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {projectMetrics.map((metric) => (
                <div key={metric} className="rounded-md border border-border/70 p-2.5 text-xs bg-card">
                  <p className="font-semibold text-foreground">{metric}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Fuente canónica identificada</p>
                </div>
              ))}
            </div>
          </FinanceSection>

          <FinanceSection
            title="Comparativa de coste"
            description="Cadena trazable de imputación de costes"
            icon={GitCompareArrows}
          >
            <div className="space-y-2.5">
              {futureCostChain.map((step, index) => (
                <div key={step}>
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="h-5 w-5 rounded-full p-0 flex items-center justify-center text-[11px] font-bold">
                      {index + 1}
                    </Badge>
                    <span className="text-xs font-semibold text-foreground">{step}</span>
                  </div>
                  {index < futureCostChain.length - 1 ? <Separator className="ml-2.5 my-1.5" /> : null}
                </div>
              ))}
            </div>
          </FinanceSection>
        </div>

        <Dialog open={detailBudgetId !== null} onOpenChange={(open) => !open && setDetailBudgetId(null)}>
          <DialogContent
            className="flex max-h-none max-w-none flex-col overflow-hidden"
            style={{
              position: "fixed",
              left: detailBudgetBounds.left,
              top: detailBudgetBounds.top,
              width: detailBudgetBounds.width,
              height: detailBudgetBounds.height,
              maxWidth: "none",
              maxHeight: "none",
              transform: "none",
              translate: "none",
              boxSizing: "border-box",
            }}
          >
            <DialogHeader>
              <DialogTitle>Presupuesto y margen por expediente</DialogTitle>
              <DialogDescription>
                {detail.data
                  ? `Exp. ${detail.data.installation.clientId || detail.data.installationId} · ${detail.data.installation.clientName || "Cliente sin nombre"} · ${detail.data.reference} · v${detail.data.version}`
                  : "Detalle del presupuesto, gastos imputados y beneficio por categoría."}
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 min-w-0 flex-1 overflow-auto">
            {detail.isLoading ? (
              <div className="flex min-h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : detail.error ? (
              <p className="py-8 text-center text-sm text-destructive">{detail.error.message}</p>
            ) : detail.data ? (
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    ["Presupuesto de venta", formatCents(detail.data.sourceBaseCents)],
                    ["Gasto real", formatCents(detail.data.actualCostCents)],
                    ["Beneficio real", formatCents(detail.data.budgetActualMarginCents)],
                    ["Margen real", formatPercent(detail.data.budgetActualMarginBps == null ? null : detail.data.budgetActualMarginBps / 100)],
                  ].map(([label, value]) => (
                    <Card key={label}>
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <section className="space-y-2">
                  <div>
                    <h3 className="font-semibold">Presupuesto frente a gasto real</h3>
                    <p className="text-sm text-muted-foreground">Los gastos registrados son costes canónicos; el ajuste manual se suma una sola vez al margen y queda auditado.</p>
                  </div>
                  <div className="max-w-full overflow-x-auto rounded-md border">
                    <Table className="min-w-[1180px]">
                      <TableHeader><TableRow>
                        <TableHead>Categoría</TableHead>
                        <TableHead className="text-right">Presupuesto venta</TableHead>
                        <TableHead className="text-right">Coste ODS</TableHead>
                        <TableHead className="text-right">Gastos registrados</TableHead>
                        <TableHead>Ajuste manual</TableHead>
                        <TableHead className="text-right">Gasto real</TableHead>
                        <TableHead className="text-right">Beneficio</TableHead>
                        <TableHead className="text-right">Margen</TableHead>
                        <TableHead className="text-right">Ahorro / sobrecoste</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {marginCategories.map((category: any) => {
                          const draft = adjustmentDrafts[category.code] ?? { amount: "0.00", note: "" };
                          const amountCents = parseEuroDraftToCents(draft.amount);
                          const canAdjust = detail.data.current && detail.data.extractionStatus === "confirmed";
                          return (
                            <TableRow key={category.code}>
                              <TableCell className="font-semibold">{category.label}</TableCell>
                              <TableCell className="text-right tabular-nums">{formatCents(category.salesBudgetCents)}</TableCell>
                              <TableCell className="text-right tabular-nums">{formatCents(category.estimatedCostCents)}</TableCell>
                              <TableCell className="text-right tabular-nums">{formatCents(category.registeredCostCents)}</TableCell>
                              <TableCell>
                                <div className="flex min-w-[220px] items-center gap-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="h-8 w-28 text-right tabular-nums"
                                    aria-label={`Ajuste manual de ${category.label}`}
                                    value={draft.amount}
                                    disabled={!canAdjust}
                                    onChange={(event) => setAdjustmentDrafts((current) => ({
                                      ...current,
                                      [category.code]: { ...draft, amount: event.target.value },
                                    }))}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title={`Guardar ajuste de ${category.label}`}
                                    aria-label={`Guardar ajuste de ${category.label}`}
                                    disabled={!canAdjust || saveMarginAdjustment.isPending || amountCents === null}
                                    onClick={() => saveMarginAdjustment.mutate({
                                      budgetId: detail.data!.id,
                                      category: category.code,
                                      amountCents: amountCents ?? 0,
                                      note: draft.note || null,
                                    })}
                                  ><Save className="h-4 w-4" /></Button>
                                </div>
                                <Input
                                  className="mt-1 h-7 w-full min-w-[200px] text-xs"
                                  placeholder="Motivo del ajuste (opcional)"
                                  aria-label={`Motivo del ajuste de ${category.label}`}
                                  value={draft.note}
                                  disabled={!canAdjust}
                                  onChange={(event) => setAdjustmentDrafts((current) => ({
                                    ...current,
                                    [category.code]: { ...draft, note: event.target.value },
                                  }))}
                                />
                              </TableCell>
                              <TableCell className="text-right font-semibold tabular-nums">{formatCents(category.actualCostCents)}</TableCell>
                              <TableCell className={`text-right font-semibold tabular-nums ${category.benefitCents >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatCents(category.benefitCents)}</TableCell>
                              <TableCell className="text-right tabular-nums">{formatPercent(category.marginPct)}</TableCell>
                              <TableCell className={`text-right tabular-nums ${category.savingsVsEstimatedCents != null && category.savingsVsEstimatedCents < 0 ? "text-rose-700" : "text-emerald-700"}`}>{formatCents(category.savingsVsEstimatedCents)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Dietas y gastos de técnicos se incluyen desde Gastos en «Otros gastos». Las compras se concilian por producto o referencia exacta; las no asociadas quedan en «Otros gastos» y no se asignan por aproximación.
                  </p>
                </section>

                <section className="space-y-3">
                  <div>
                    <h3 className="font-semibold">Líneas del presupuesto y compras conciliadas</h3>
                    <p className="text-sm text-muted-foreground">Las líneas se agrupan por categoría. Coste ODS usa la estimación guardada en esta versión del presupuesto, no precios actuales del catálogo.</p>
                  </div>
                  {PROJECT_MARGIN_CATEGORIES.map((category) => {
                    const categoryLines = budgetLineCategories.filter((line: any) => line.category === category.code);
                    if (!categoryLines.length) return null;
                    return (
                      <div key={category.code} className="overflow-hidden rounded-md border">
                        <div className="bg-muted/50 px-3 py-2 text-sm font-semibold">{category.label}</div>
                        <div className="max-w-full overflow-x-auto">
                          <Table className="min-w-[760px]">
                            <TableHeader><TableRow>
                              <TableHead>Línea / concepto</TableHead>
                              <TableHead className="text-right">Cantidad</TableHead>
                              <TableHead className="text-right">Venta</TableHead>
                              <TableHead className="text-right">Coste ODS</TableHead>
                              <TableHead className="text-right">Compra real asociada</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>{categoryLines.map((line: any) => (
                              <TableRow key={line.budgetLineId}>
                                <TableCell><span className="font-medium">{line.productName}</span>{line.description ? <span className="block text-xs text-muted-foreground">{line.description}</span> : null}</TableCell>
                                <TableCell className="text-right tabular-nums">{line.quantity}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatCents(line.saleTotalCents)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatCents(line.estimatedCostCents)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatCents(line.actualCostCents)}</TableCell>
                              </TableRow>
                            ))}</TableBody>
                          </Table>
                        </div>
                      </div>
                    );
                  })}
                </section>
              </div>
            ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailBudgetId(null)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

/**
 * @deprecated Unreachable compatibility reference only.
 * Not exported by this module or any route/navigation entry.
 * Removal backlog: GINES financeBudgetBaselines retirement after data migration.
 */
function LegacyFinanceBudgetBaselinesView() {
  const { user } = useAuth();
  const allowed = user ? canAccessFinanceRoute("/accounting/budgets", user.role) : false;
  const utils = trpc.useUtils();
  const query = trpc.finance.budgets.list.useQuery(undefined, { enabled: allowed });
  const approveMutation = trpc.finance.budgets.approve.useMutation({
    onSuccess: async () => {
      toast.success("Presupuesto base aprobado correctamente");
      await utils.finance.budgets.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });
  const [draftAmounts, setDraftAmounts] = useState<Record<number, string>>({});

  if (!allowed) return <AccessDenied title="Presupuestos" />;

  const rows = query.data?.rows ?? [];
  const totals = query.data?.totals;

  const approveBaseline = (row: any) => {
    const amountText = draftAmounts[row.installationId]
      ?? formatCentsForInput(row.currentBaseline?.approvedCostCents ?? row.candidateApprovedCostCents);
    const approvedCostCents = parseEuroDraftToCents(amountText);
    if (approvedCostCents === null) {
      toast.error("Importe de presupuesto invalido");
      return;
    }
    approveMutation.mutate({
      installationId: row.installationId,
      approvedCostCents,
      notes: "Baseline aprobada desde Finanzas > Presupuestos",
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <ClipboardList className="h-4 w-4" />
              Finanzas y Contabilidad
            </div>
          <h1 className="text-2xl font-semibold">Presupuestos</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Baseline versionada del presupuesto de coste aprobado por expediente, comparada contra el coste real imputado.
          </p>
        </div>
        <Badge variant="outline" className="w-fit">Actualizado {formatDateTime(query.data?.updatedAt)}</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <HelpCircle className="h-4 w-4" />
            Terminología de presupuestos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-md border p-3 text-sm">
                  <p className="font-medium">Presupuesto aprobado</p>
                  <p className="mt-1 text-xs text-muted-foreground">Coste previsto autorizado para ejecutar el expediente</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Importe autorizado como referencia de coste para el proyecto</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-md border p-3 text-sm">
                  <p className="font-medium">Coste real</p>
                  <p className="mt-1 text-xs text-muted-foreground">Costes ya imputados desde facturas, dietas y otros gastos</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Suma de costes realmente incurridos y registrados</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-md border p-3 text-sm">
                  <p className="font-medium">Desviacion</p>
                  <p className="mt-1 text-xs text-muted-foreground">Coste real - presupuesto aprobado</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Diferencia entre el coste real y el presupuesto aprobado. Positivo = sobrecoste</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-md border p-3 text-sm">
                  <p className="font-medium">Uso del presupuesto</p>
                  <p className="mt-1 text-xs text-muted-foreground">Coste real / presupuesto aprobado</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Porcentaje del presupuesto consumido por costes reales</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-md border p-3 text-sm">
                  <p className="font-medium">Margen previsto</p>
                  <p className="mt-1 text-xs text-muted-foreground">Importe contractual - presupuesto aprobado</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Margen esperado basado en el contrato y el presupuesto aprobado</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-md border p-3 text-sm">
                  <p className="font-medium">Margen real</p>
                  <p className="mt-1 text-xs text-muted-foreground">Ingreso economico computable - coste real</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Margen real obtenido tras deducir los costes efectivamente incurridos</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-amber-50/60">
        <CardContent className="flex flex-col gap-3 px-4 py-4 text-sm text-amber-950 md:flex-row md:items-start">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div className="space-y-1">
            <p className="font-medium">V1 con importes globales por expediente</p>
            <p>
              Esta version V1 trabaja con importes globales por expediente. La comparacion por producto/BOM se incorporara en una fase posterior.
            </p>
          </div>
        </CardContent>
      </Card>

        {query.isLoading ? (
          <Card>
            <CardContent className="px-4 py-8 text-sm text-muted-foreground">Cargando presupuestos versionados...</CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="flex flex-col items-start gap-3 px-4 py-6">
              <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                <AlertCircle className="h-4 w-4" />
                Error al cargar presupuestos
              </div>
              <p className="text-sm text-muted-foreground">{query.error.message}</p>
              <Button variant="outline" size="sm" onClick={() => query.refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reintentar
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-4">
              <Card className="min-w-0">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Baselines vigentes</p>
                  <p className="mt-1 text-xl font-semibold">{totals?.baselineCount ?? 0}</p>
                </CardContent>
              </Card>
              <Card className="min-w-0">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Presupuesto aprobado</p>
                  <p className="mt-1 text-xl font-semibold">{formatCents(totals?.approvedCostCents)}</p>
                </CardContent>
              </Card>
              <Card className="min-w-0">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Coste real</p>
                  <p className="mt-1 text-xl font-semibold text-rose-700">{formatCents(totals?.actualCostCents)}</p>
                </CardContent>
              </Card>
              <Card className="min-w-0">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Desviacion</p>
                  <p className="mt-1 text-xl font-semibold">{formatCents(totals?.budgetDeviationCents)}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileClock className="h-4 w-4" />
                  Baselines por expediente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No hay instalaciones con contrato, coste candidato, baseline o coste real.</p>
                ) : rows.map((row: any) => {
                  const defaultAmount = formatCentsForInput(row.currentBaseline?.approvedCostCents ?? row.candidateApprovedCostCents);
                  const draftValue = draftAmounts[row.installationId] ?? defaultAmount;
                  const hasApprovedBaseline = !!row.currentBaseline?.approvedAt;
                  const isEditable = !hasApprovedBaseline;
                  return (
                    <div key={row.installationId} className="rounded-md border p-3">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">Exp. {row.expedienteCode || row.installationId}</Badge>
                            {row.currentBaseline?.approvedAt ? (
                              <Badge variant="default" className="bg-green-600 hover:bg-green-700">Aprobado v{row.currentBaseline.version}</Badge>
                            ) : row.candidateApprovedCostCents ? (
                              <Badge variant="secondary">Borrador</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">Sin presupuesto base</Badge>
                            )}
                            {row.currentBaseline?.replacedAt && (
                              <Badge variant="outline" className="text-muted-foreground">Sustituido</Badge>
                            )}
                            <span className="text-sm font-medium">{row.clientName || "Cliente sin nombre"}</span>
                          </div>
                          <p className="mt-1 truncate text-xs text-muted-foreground" title={row.workDescription || ""}>
                            {row.workDescription || "Sin descripcion de obra"}
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => { window.location.href = `/installations/${row.installationId}`; }}>
                          Abrir instalación
                        </Button>
                      </div>

                      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
                        <div>
                          <p className="text-xs text-muted-foreground">Contrato</p>
                          <p className="text-sm font-medium">{formatCents(row.contractAmountCents)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Presupuesto aprobado</p>
                          <p className="text-sm font-medium">{formatCents(row.currentBaseline?.approvedCostCents)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Coste real</p>
                          <p className="text-sm font-medium text-rose-700">{formatCents(row.actualCostCents)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Desviacion</p>
                          <p className="text-sm font-medium">{formatCents(row.budgetDeviationCents)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Uso presupuesto</p>
                          <p className="text-sm font-medium">{formatPercent(row.budgetUsedPct)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Margen previsto</p>
                          <p className="text-sm font-medium">{formatCents(row.plannedMarginCents)}</p>
                        </div>
                      </div>

                      <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(180px,240px)_auto] lg:items-end">
                        <div>
                          <p className="mb-1 text-xs text-muted-foreground">Nuevo presupuesto aprobado (€)</p>
                          <Input
                            inputMode="decimal"
                            value={draftValue}
                            onChange={(event) => setDraftAmounts((current) => ({ ...current, [row.installationId]: event.target.value }))}
                            disabled={!isEditable}
                            placeholder={isEditable ? "0.00" : formatCents(row.currentBaseline?.approvedCostCents)}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {!row.currentBaseline?.approvedAt && row.candidateApprovedCostCents && (
                            <Button
                              size="sm"
                              onClick={() => approveBaseline(row)}
                              disabled={approveMutation.isPending}
                            >
                              {approveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                              Aprobar presupuesto base
                            </Button>
                          )}
                          {!row.candidateApprovedCostCents && !row.currentBaseline?.approvedAt && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDraftAmounts((current) => ({ ...current, [row.installationId]: formatCentsForInput(row.contractAmountCents) }))}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Crear presupuesto base
                            </Button>
                          )}
                          {row.currentBaseline?.approvedAt && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDraftAmounts((current) => ({ ...current, [row.installationId]: formatCentsForInput(row.contractAmountCents) }))}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Crear nueva version
                            </Button>
                          )}
                          {row.currentBaseline?.approvedAt && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled
                                >
                                  <History className="mr-2 h-4 w-4" />
                                  Ver historial
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Historial de versiones disponible en fase posterior</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <Button variant="outline" size="sm" onClick={() => { window.location.href = `/installations/${row.installationId}`; }}>
                            Abrir instalacion
                          </Button>
                          <span className="self-center text-xs text-muted-foreground">
                            {row.currentBaseline?.approvedAt ? `Aprobado ${formatDateTime(row.currentBaseline.approvedAt)}` : "Sin version aprobada"}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>{row.sourceCounts.supplierInvoiceAllocations} imputaciones proveedor</span>
                        <span>{row.sourceCounts.technicianExpenses} dietas/tickets</span>
                        <span>{row.sourceCounts.manualExpenses} gastos manuales/importados</span>
                        {row.budgetReference ? <span>Ref. {row.budgetReference}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Boxes className="h-4 w-4" />
                Siguiente nivel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>La BOM futura conecta oficina tecnica, pedidos, recepciones y facturas proveedor.</p>
              <Separator />
              <p>Las desviaciones se registran como senales, no como ediciones silenciosas.</p>
              <Separator />
              <p>Product Catalog recibira retroalimentacion para futuros precios, no cambios retroactivos.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <GitCompareArrows className="h-4 w-4" />
                Principios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {budgetPrinciples.map((principle) => (
                  <li key={principle} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{principle}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
